import useFocusTrap from '@hooks/useFocusTrap';
import clsx from 'clsx';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

import styles from './Modal.module.scss';

interface Props {
  id: string;
  isOpen: boolean;
  onClose: () => void;
  isPanel?: boolean;
  modalClassName?: string;
}

export default function Modal({
  id,
  isOpen,
  onClose,
  isPanel = false,
  modalClassName,
  children,
}: PropsWithChildren<Props>) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelCloseRef = useRef<HTMLButtonElement | null>(null);
  const { enableFocusTrap, disableFocusTrap } = useFocusTrap(
    panelRef,
    panelCloseRef,
  );

  useEffect(() => {
    if (isOpen) {
      enableFocusTrap();
    } else {
      disableFocusTrap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <div id={id}>
      <div
        className={clsx(styles.backdrop, isOpen && styles.showBackdrop)}
        onClick={onClose}
        onKeyDown={onClose}
      />

      <CSSTransition
        in={isOpen}
        timeout={300}
        unmountOnExit
        classNames={{
          enter: styles.fadeEnter,
          enterActive: styles.fadeEnterActive,
          exit: styles.fadeExit,
          exitActive: styles.fadeExitActive,
        }}
      >
        <div
          className={clsx(
            styles.modal,
            isPanel && styles.isPanel,
            modalClassName,
          )}
          ref={panelRef}
        >
          {children}
        </div>
      </CSSTransition>
    </div>
  );
}
